import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useTranslation } from 'react-i18next';
import config from '../../../../config';
import { useCreateSessionMutation } from '../../../../generated/core-api/graphql-react-query';
import useGlobalStoreContext from '../../../Contexts/GlobalContext/Contexts/GlobalStoreContext/useGlobalStoreContext';
import useAudioDeviceConfig from '../../../Hooks/audio/useAudioDeviceConfig';
import { useOrganisationData } from '../../../Hooks/useOrganisation';
import useIsAppEnabled from '../../ApplicationSyncContext/useIsAppEnabled';
import useAudioConfigContext from '../../AudioConfigContext/useAudioConfigContext';
import useMultiLanguageContext from '../../MultiLanguageContext/useMultiLanguageContext';
import { useDualChannelDevices } from '../Contexts/SessionStoreContextProvider/SessionStoreContextProvider';
import useSessionStoreContext from '../Contexts/SessionStoreContextProvider/useSessionStoreContext';

function useCreateAudioParams() {
  const audioConfig = useAudioConfigContext();

  const monoChannelDeviceParams = React.useMemo(() => {
    const currentDevice = audioConfig?.devices.find((d) => d.deviceId === audioConfig.deviceId);
    return {
      inputDevice: currentDevice?.label,
      inputDeviceOptions: audioConfig?.devices.map((d) => d.label),
    };
  }, [audioConfig]);

  const orgData = useOrganisationData();
  const isAppEnabled = useIsAppEnabled();

  const isDualChannel = React.useMemo(() => !!isAppEnabled && !!orgData.features.cogneedAi?.dualChannel?.enabled, [isAppEnabled, orgData]);

  const dualChannelDevices = useDualChannelDevices();

  const dualChannelDeviceParams = React.useMemo(() => {
    const inputDevice = dualChannelDevices?.input.options.find((d) => d.id === dualChannelDevices.input.selected)?.name;
    const outputDevice = dualChannelDevices?.output.options.find((d) => d.id === dualChannelDevices.output.selected)?.name;
    return {
      inputDevice,
      outputDevice,
      inputDeviceOptions: dualChannelDevices?.input.options.map((d) => d.name),
      outputDeviceOptions: dualChannelDevices?.output.options.map((d) => d.name),
    };
  }, [dualChannelDevices]);

  return React.useMemo(() => (isDualChannel ? dualChannelDeviceParams : monoChannelDeviceParams), [isDualChannel, dualChannelDeviceParams, monoChannelDeviceParams]);
}

export default function useStartSession() {
  const { sessionId: [, setSessionId], sessionLoading: [, setSessionLoading] } = useSessionStoreContext();
  const queryClient = useQueryClient();
  const { projectId: [projectId, setProjectId] } = useGlobalStoreContext();
  const { language } = useMultiLanguageContext();
  const isAppEnabled = useIsAppEnabled();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('notifications');
  const { disabled: audioDisabled } = useAudioDeviceConfig();

  const audioParams = useCreateAudioParams();
  const audioParamsRef = React.useRef(audioParams);
  React.useEffect(() => {
    audioParamsRef.current = audioParams;
  }, [audioParams]);

  const startMutation = useCreateSessionMutation({
    onSuccess(data) {
      setSessionLoading(false);
      setSessionId(data.session.create.id);
      queryClient.setQueryData(
        ['getLiveSession', { input: { id: data.session.create.id } }],
        { getSession: { ...data.session.create, detections: [] } },
      );
    },
    onError(error) {
      setSessionLoading(false);
      enqueueSnackbar(t('session-create-error'), { variant: 'error' });
      console.error('Error creating new session', error);
    },
  });

  const startSession = React.useCallback(async (args: { projectId?: string, externalCallId?: string, id?: string }) => {
    setSessionLoading(true);
    const { projectId: sessionProjectId, externalCallId, id } = args;
    if (sessionProjectId && sessionProjectId !== projectId) { setProjectId(sessionProjectId); }
    const effectiveProjectId = sessionProjectId ?? projectId;
    if (!effectiveProjectId || !language) {
      setSessionLoading(false);
      enqueueSnackbar(t(!effectiveProjectId ? 'session-create-error-missing-project' : 'session-create-error-missing-language'), { variant: 'error' });
      console.error('Error creating new session: missing required session creation input', {
        projectId: effectiveProjectId,
        language,
      });
      return;
    }

    if (audioDisabled) {
      setSessionLoading(false);
      enqueueSnackbar(t('session-create-error-no-audio-device'), { variant: 'error' });
      console.error('Error creating new session: no audio device is available', {
        projectId: effectiveProjectId,
        externalCallId,
        isDualChannel: isAppEnabled,
      });
      return;
    }

    let dualChannelAppVersion: string | undefined;

    if (isAppEnabled && window.utilsAPI) {
      try {
        dualChannelAppVersion = await window.utilsAPI.getVersion();
      } catch (error) {
        console.error('Error getting dual-channel app version', error);
      }
    }

    startMutation.mutate({
      input: {
        projectId: effectiveProjectId,
        externalCallId,
        id,
        language,
        isDualChannel: isAppEnabled,
        liveAppVersion: config.AppVersion,
        ...dualChannelAppVersion && { dualChannelAppVersion },
        ...audioParamsRef.current,
      },
    });
  }, [audioDisabled, enqueueSnackbar, isAppEnabled, language, projectId, setProjectId, setSessionLoading, startMutation, t]);

  return startSession;
}
